import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { Ability, subject } from '@casl/ability';
import { MatDialog } from '@angular/material/dialog';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import { AddContractorDialogComponent } from '../../contractor-details/add-contractor-dialog/add-contractor-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../../shared/components';
import { DailyReportService } from '../../../../daily-report.service';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { ItemPostingLineItemComponent } from '../item-posting-line-item/item-posting-line-item.component';

@Component({
  standalone: false,
  selector: 'app-item-postings-details',
  templateUrl: './item-postings-details.component.html',
  styleUrls: ['./item-postings-details.component.scss'],
})
export class ItemPostingsDetailsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @ViewChildren(ItemPostingLineItemComponent) lineItemComponents: QueryList<ItemPostingLineItemComponent>;

  lineItems: any[] = [];
  lineItemForms: { [key: string]: FormGroup } = {};
  selectedLineItemId: string = null;
  contractorOptions: any[] = [];
  missingPermittedContractors: any[] = [];
  showAddContractorButton = false;

  quantityTotal: number = 0;
  savedQuantityTotal = 0;
  siteQuantities: any[] = [];
  consumptionHint = '';
  consumptionHintColor = '';

  bidItem: any;
  areaCalculationGroups: any[] = [];
  projectLookups: any = {};
  // Surfaced to the line-item template so it can mount the embedded Sample
  // Records affordance with the right scope. Both come from the existing
  // dailyReport / projectDetails store streams below.
  projectId: string;
  dailyReportId: number | string;
  editable = false;

  private projectData: any;
  private reportData: any;
  private pendingContractorId: string = null;
  private existingDataLoaded = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private dialog: MatDialog,
    private dailyReportService: DailyReportService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private ability: Ability,
  ) {
  }

  ngOnInit() {
    // Listen for save trigger from parent
    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    // Listen for delete trigger from parent
    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dailyReportService.delete.next({
          type: 'bidItems',
          id: this.data?.itemPostings?.id,
        });
      });

    // Listen for add button click from actions section
    this.dailyReportService.add
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.addLineItem();
      });

    // Pictures/videos uploaded via + button
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (!this.selectedLineItemId || !this.lineItemForms[this.selectedLineItemId]) return;
        const form = this.lineItemForms[this.selectedLineItemId];
        const items = Array.isArray(data) ? data : [data];

        items.forEach(item => {
          if (item?.fileType === 'picture' || item?.fileType === 'document') {
            const pictures = JSON.parse(form.controls['pictures']?.value || '[]');
            pictures.push(item);
            form.controls['pictures'].setValue(JSON.stringify(pictures));
          }
          if (item?.fileType === 'video') {
            const videos = JSON.parse(form.controls['videos']?.value || '[]');
            videos.push(item);
            form.controls['videos'].setValue(JSON.stringify(videos));
          }
        });
      });

    // Comment added via + button
    this.dailyReportService.addComment
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (!this.selectedLineItemId || !this.lineItemForms[this.selectedLineItemId]) return;
        const form = this.lineItemForms[this.selectedLineItemId];
        if (!form.controls['comment']) {
          form.addControl('comment', new FormControl(null));
        }
      });

    // Location selected via + button
    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe(location => {
        if (!this.selectedLineItemId || !this.lineItemForms[this.selectedLineItemId]) return;
        this.lineItemForms[this.selectedLineItemId].patchValue(location);
        if (location?.locationText !== undefined) {
          this.lineItemForms[this.selectedLineItemId].controls['location']?.setValue(location.locationText);
        }
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.projectData = project;
        this.bidItem = project?.bidSheet?.bidItems?.find(bi => bi.id === this.data?.itemPostings?.id);
        this.areaCalculationGroups = this.bidItem?.groups || [];
        this.projectLookups = project?.lookups || {};

        this.updateConsumptionHint();
        this.buildContractorOptions();
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.reportData = report;
        // `dailyReport.id` is the daily_report_id used everywhere else (see
        // projects.effects.ts and report-bid-item.component.ts which both
        // read `dailyReport.id`). Capturing it here lets the line-item
        // children mount the embedded Sample Records list with the right scope.
        this.dailyReportId = report?.id || this.dailyReportId;
        // The dailyReport state already normalizes project_id from the various
        // shapes (project_id || project.id || site.project_id || site.project.id),
        // see projects.effects.ts FetchDailyReportSuccess.
        this.projectId = report?.project_id || report?.project?.id || report?.site?.project?.id || this.projectId;
        this.editable = this.ability.can('create', subject('DailyReport', { ...report }));
        this.buildContractorOptions();
        this.loadExistingLineItems();
      });

    // Fetch site quantities for consumption info
    const bidItemId = this.data?.itemPostings?.id;
    if (bidItemId) {
      this.projectsService.fetchBidItemsSitesList({
        bidItemId,
        qp: { include: ['site_quantities'] },
      }).pipe(takeUntil(this.onDestroy)).subscribe((response: any) => {
        this.siteQuantities = response?.data?.site_quantities || [];
        this.updateConsumptionHint();
      });
    }
  }

  addLineItem() {
    const id = `new_${new Date().getTime()}`;
    const now = new Date();
    this.lineItems.push({
      id,
      created_at: now.toISOString(),
    });
    const form = new FormGroup({
      quantity: new FormControl(null),
      contractor: new FormControl(null, [Validators.required]),
      pictures: new FormControl(JSON.stringify([])),
      videos: new FormControl(JSON.stringify([])),
      latitude: new FormControl(null),
      longitude: new FormControl(null),
      location: new FormControl(null),
      materialSetId: new FormControl(null),
      materials: new FormControl(JSON.stringify([])),
      groupValues: new FormControl(JSON.stringify([])),
      alignment: new FormControl(null),
      stationRangeFromStation: new FormControl(null),
      stationRangeFromOffsetType: new FormControl(null),
      stationRangeFromOffsetDistance: new FormControl(null),
      stationRangeToStation: new FormControl(null),
      stationRangeToOffsetType: new FormControl(null),
      stationRangeToOffsetDistance: new FormControl(null),
    });
    form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.calculateQuantityTotal();
        this.projectsService.isAnyFormDirty.next(true);
      });
    this.lineItemForms[id] = form;
    this.selectedLineItemId = id;
    // Accordion open = enable + button
    this.dailyReportService.disableAddMenu.next(false);
  }

  private loadExistingLineItems() {
    if (this.existingDataLoaded || !this.reportData) return;
    this.existingDataLoaded = true;

    const bidItemId = this.data?.itemPostings?.id;
    const formattedBidItems = this.reportData?.reportDetails?.bidItems || [];
    const match = formattedBidItems.find(bi => bi.id === bidItemId);
    const existingLineItems = match?.lineItems || [];

    if (existingLineItems.length === 0) {
      // No saved data — add a default empty line item
      this.addLineItem();
      setTimeout(() => this.projectsService.isAnyFormDirty.next(false), 100);
      return;
    }

    // Load each saved line item
    existingLineItems.forEach(li => {
      const id = li.id;
      this.lineItems.push({
        id,
        created_at: li.created_at,
        station_range: li.station_range || null,
        location: li.location || null,
        latitude: li.latitude || null,
        longitude: li.longitude || null,
        // No design-context UI on item postings, but the line keeps its link (key presence = untouched).
        ...(li.map_feature_link !== undefined ? { map_feature_link: li.map_feature_link } : {}),
      });

      const stationRange = li.station_range;
      const formControls: any = {
        quantity: new FormControl(li.quantity ?? null),
        contractor: new FormControl(li.sub_contractor?.id ?? null, [Validators.required]),
        pictures: new FormControl(JSON.stringify(li.pictures || [])),
        videos: new FormControl(JSON.stringify(li.videos || [])),
        latitude: new FormControl(li.latitude || null),
        longitude: new FormControl(li.longitude || null),
        location: new FormControl(li.location || null),
        materialSetId: new FormControl(li.materials?.[0]?.bid_item_material_set?.id ?? null),
        materials: new FormControl(JSON.stringify(li.materials || [])),
        groupValues: new FormControl(JSON.stringify(li.group_values || [])),
        alignment: new FormControl(stationRange?.alignment ?? null),
        stationRangeFromStation: new FormControl(stationRange?.from?.station ?? null),
        stationRangeFromOffsetType: new FormControl(stationRange?.from?.offset_type ?? null),
        stationRangeFromOffsetDistance: new FormControl(stationRange?.from?.offset_distance ?? null),
        stationRangeToStation: new FormControl(stationRange?.to?.station ?? null),
        stationRangeToOffsetType: new FormControl(stationRange?.to?.offset_type ?? null),
        stationRangeToOffsetDistance: new FormControl(stationRange?.to?.offset_distance ?? null),
      };

      if (li.comment) {
        formControls.comment = new FormControl(li.comment);
      }

      const form = new FormGroup(formControls);

      form.valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe(() => {
          this.calculateQuantityTotal();
          this.projectsService.isAnyFormDirty.next(true);
        });

      this.lineItemForms[id] = form;
    });

    // Calculate saved total for consumption offset
    this.savedQuantityTotal = existingLineItems.reduce((sum, li) => sum + (Number(li.quantity) || 0), 0);
    this.calculateQuantityTotal();

    // Select and expand the first line item
    if (this.lineItems.length > 0) {
      this.selectedLineItemId = this.lineItems[0].id;
      this.dailyReportService.disableAddMenu.next(false);
    }

    // Reset dirty flag after initial load (form creation triggers valueChanges)
    setTimeout(() => this.projectsService.isAnyFormDirty.next(false));
  }

  deleteLineItem(id: string) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        'Are you sure you want to delete?',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.lineItems = this.lineItems.filter(li => li.id !== id);
        delete this.lineItemForms[id];
        this.calculateQuantityTotal();
        this.projectsService.isAnyFormDirty.next(true);
        if (this.selectedLineItemId === id) {
          this.selectedLineItemId = null;
          this.dailyReportService.disableAddMenu.next(true);
        }
      }
    });
  }

  selectLineItem(id: string) {
    this.selectedLineItemId = this.selectedLineItemId === id ? null : id;
    // + button (location/picture): enabled when accordion is open, disabled when closed
    this.dailyReportService.disableAddMenu.next(!this.selectedLineItemId);
  }

  private buildContractorOptions() {
    if (!this.projectData || !this.reportData) return;

    const selectedBidItemId = this.data?.itemPostings?.id;
    const bidSheetItem = this.projectData?.bidSheet?.bidItems?.find(bi => bi.id === selectedBidItemId);
    const allPermitted = bidSheetItem?.all_permitted_contractors || [];

    const reportContractors = this.reportData?.reportDetails?.sub_contractors || [];
    const reportIds = new Set(reportContractors.map(sc => sc.sub_contractor?.id));

    if (reportContractors.length === 0) {
      // No contractors on report yet — show all permitted contractors
      this.contractorOptions = [...allPermitted];
      this.missingPermittedContractors = [];
    } else {
      this.contractorOptions = allPermitted.filter(c => reportIds.has(c.value));
      this.missingPermittedContractors = allPermitted.filter(c => !reportIds.has(c.value));
    }
    this.showAddContractorButton = this.missingPermittedContractors.length > 0;

    if (this.pendingContractorId && this.contractorOptions.some(c => c.value === this.pendingContractorId)) {
      // Set on the currently selected line item's form
      if (this.selectedLineItemId && this.lineItemForms[this.selectedLineItemId]) {
        this.lineItemForms[this.selectedLineItemId].controls['contractor'].setValue(this.pendingContractorId);
      }
      this.pendingContractorId = null;
    }
  }

  openAddContractorDialog() {
    const dialogRef = this.dialog.open(AddContractorDialogComponent, {
      disableClose: true,
      width: '450px',
      data: {
        restrictedContractors: this.missingPermittedContractors,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.sub_contractor_id) {
        this.pendingContractorId = result.sub_contractor_id;
      }
    });
  }

  calculateQuantityTotal() {
    this.quantityTotal = Object.values(this.lineItemForms).reduce((sum, form) => {
      return sum + (Number(form.controls['quantity']?.value) || 0);
    }, 0);
    this.updateConsumptionHint();
  }

  private getSiteQuantity(): any {
    const siteId = this.reportData?.reportDetails?.site_id;
    return siteId && this.siteQuantities?.find(sq => sq.site_id === siteId);
  }

  getCapacity(): number | null {
    if (!this.bidItem) return null;
    const siteQty = this.getSiteQuantity();
    const raw = siteQty ? siteQty.quantity : this.bidItem.quantity;
    const total = parseFloat(raw);
    return !isNaN(total) ? total : null;
  }

  private getConsumed(): number {
    const siteQty = this.getSiteQuantity();
    const rawConsumed = siteQty ? siteQty.consumed : this.bidItem?.consumed;
    const serverConsumed = parseFloat(rawConsumed) || 0;
    const currentOffset = (Number(this.quantityTotal) || 0) - this.savedQuantityTotal;
    return serverConsumed + currentOffset;
  }

  getRemaining(): number {
    return (this.getCapacity() ?? 0) - this.getConsumed();
  }

  getOverLimit(): number {
    return Math.abs(Math.min(0, this.getRemaining()));
  }

  getQuantityRounding(): number {
    return this.bidItem?.quantity_rounding ?? 2;
  }

  private updateConsumptionHint() {
    const capacity = this.getCapacity();
    if (capacity == null) {
      this.consumptionHint = '';
      return;
    }
    const format = `1.0-${this.getQuantityRounding()}`;
    const pipe = new DecimalPipe('en-US');
    const remaining = this.getRemaining();
    if (remaining >= 0) {
      this.consumptionHint = `${pipe.transform(remaining, format)} of ${pipe.transform(capacity, format)} remaining`;
      this.consumptionHintColor = '';
    } else {
      this.consumptionHint = `0 of ${pipe.transform(capacity, format)} remaining, ${pipe.transform(this.getOverLimit(), format)} over limit`;
      this.consumptionHintColor = '#E53935';
    }
  }

  save() {
    // Validate: each line item must have contractor and location or station
    let hasValidationError = false;
    const lineItemComponentsArray = this.lineItemComponents?.toArray() || [];

    let firstInvalidComp: any = null;

    lineItemComponentsArray.forEach((comp, index) => {
      // Contractor validation
      comp.checkForErrors();
      if (comp.errorMessages['contractor']) {
        if (!hasValidationError) {
          this.selectedLineItemId = this.lineItems[index]?.id;
          this.dailyReportService.disableAddMenu.next(false);
          firstInvalidComp = comp;
        }
        hasValidationError = true;
      }

      // Location/station validation
      if (!comp.hasLocationOrStation) {
        comp.showLocationStationError = true;
        if (!hasValidationError) {
          this.selectedLineItemId = this.lineItems[index]?.id;
          this.dailyReportService.disableAddMenu.next(false);
          firstInvalidComp = comp;
        }
        hasValidationError = true;
      } else {
        comp.showLocationStationError = false;
      }
    });

    if (hasValidationError) {
      firstInvalidComp?.scrollToFirstError();
      return;
    }

    const bidItemId = this.data?.itemPostings?.id;

    const lineItemsPayload = this.lineItems.map(li => {
      const form = this.lineItemForms[li.id];
      if (!form) return null;
      const v = form.getRawValue();

      const materials = JSON.parse(v.materials || '[]').map(m => ({
        bid_item_material_set_id: m.bid_item_material_set?.id || m.bid_item_material_set_id,
        bid_item_material_set_material_id: m.bid_item_material_set_material?.id || m.bid_item_material_set_material_id,
        material_id: m.material?.id || m.material_id,
        quantity: m.quantity,
        group_values: m.group_values || null,
      }));

      const pictures = JSON.parse(v.pictures || '[]');
      const videos = JSON.parse(v.videos || '[]');
      const groupValues = JSON.parse(v.groupValues || '[]');

      const stationRange = (v.alignment || v.stationRangeFromStation || v.stationRangeToStation) ? {
        from: {
          station: v.stationRangeFromStation,
          offset_type: v.stationRangeFromOffsetType,
          offset_distance: v.stationRangeFromOffsetDistance,
        },
        to: {
          station: v.stationRangeToStation,
          offset_type: v.stationRangeToOffsetType,
          offset_distance: v.stationRangeToOffsetDistance,
        },
        alignment: v.alignment || null,
      } : null;

      return {
        id: li.id,
        created_at: li.created_at,
        bid_item_id: bidItemId,
        quantity: v.quantity,
        sub_contractor_id: v.contractor,
        pictures,
        videos,
        latitude: v.latitude,
        longitude: v.longitude,
        location: v.location || null,
        materials,
        group_values: groupValues,
        station_range: stationRange,
        comment: v.comment || null,
        // Re-sent as held; the whole-report save converts it (withLinkPayloads), null once the pin is gone.
        ...(li.map_feature_link !== undefined ? { map_feature_link: li.map_feature_link } : {}),
      };
    }).filter(Boolean);

    const savePayload = {
      type: 'bidItems',
      data: lineItemsPayload,
      options: { bidItem: this.data?.itemPostings },
    };

    this.dailyReportService.save.next(savePayload);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
